import React from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import TextInputField from "./TextInputField";
import CustomTooltip from "./CustomTooltip";
import InfoSvg from "../../../../common/svgs/InfoSvg";
import {
  getQuestionBiomarkerNames,
  getQuestionText,
  LabPanelQuestion,
} from "../../utils/labPanelQuestions";

interface LabPanelQuestionFieldsProps {
  questions: LabPanelQuestion[];
  // The list with the answer written to that question's `userAnswer`.
  onChange: (questions: LabPanelQuestion[]) => void;
  // The panel the questions belong to, for the "Asked for: <biomarkers>"
  // icon on a question that concerns some of its biomarkers (the same icon
  // the summary card shows). Omit it and no icon is shown.
  panel?: any;
  // One column instead of two (narrow screens, modals).
  singleColumn?: boolean;
}

/**
 * The lab's order-entry questions as a form: one text input per question,
 * labelled with the question's friendly wording (`label`, else the lab's own
 * text) and using its accepted-answer
 * format (`constraint`) as the placeholder. Shared by the prescription
 * builder's panel editor and the prescription detail's edit-panel modal, so a
 * panel is answered the same way wherever it is prescribed. Renders nothing
 * for a panel without questions.
 */
const LabPanelQuestionFields: React.FC<LabPanelQuestionFieldsProps> = ({
  questions,
  onChange,
  panel,
  singleColumn = false,
}) => {
  const theme: Theme = useTheme();

  if (questions.length === 0) return null;

  const handleAnswerChange = (index: number, answer: string) => {
    onChange(
      questions.map((question, i) =>
        i === index ? { ...question, userAnswer: answer } : question
      )
    );
  };

  return (
    <div>
      <div style={styles.title(theme)}>{`Questions (${questions.length})`}</div>
      <div style={styles.note(theme)}>
        The lab requires an answer to each of these to process the order.
      </div>
      <div style={styles.grid(singleColumn)}>
        {questions.map((question, index) => {
          const biomarkers = panel
            ? getQuestionBiomarkerNames(panel, question)
            : [];
          return (
            <TextInputField
              key={question.id ?? question.code ?? index}
              label={getQuestionText(question)}
              placeholder={question.constraint || "Answer"}
              value={
                question.userAnswer === null ||
                question.userAnswer === undefined
                  ? ""
                  : String(question.userAnswer)
              }
              name={`lab-question-${question.id ?? index}`}
              handleChange={(event) =>
                handleAnswerChange(index, event.target.value)
              }
              endAdornment={
                biomarkers.length > 0 ? (
                  <CustomTooltip
                    inline
                    type="medium"
                    customHeight="auto"
                    message={`Asked for: ${biomarkers.join(", ")}`}
                  >
                    <span
                      style={styles.infoIcon}
                      aria-label={`Asked for ${biomarkers.join(", ")}`}
                    >
                      <InfoSvg
                        color={theme.colors_ds.NEUTRAL_ICON_WEAK}
                        size={14}
                      />
                    </span>
                  </CustomTooltip>
                ) : undefined
              }
            />
          );
        })}
      </div>
    </div>
  );
};

export default LabPanelQuestionFields;

const styles = {
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "17px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    paddingBottom: "8px",
  }),
  note: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    paddingBottom: "12px",
  }),
  infoIcon: {
    display: "inline-flex",
    alignItems: "center",
    cursor: "default",
  },
  grid: (singleColumn: boolean) => ({
    display: "grid",
    gridTemplateColumns: singleColumn ? "1fr" : "repeat(2, 1fr)",
    gap: "16px",
  }),
};
